// Shared formatting helpers for the audit log UI.

/** Maps an audit operation to a Mantine badge color. */
export function operationColor(operation: string): string {
  switch (operation) {
    case 'Create':
      return 'green'
    case 'Update':
      return 'blue'
    case 'Delete':
      return 'red'
    default:
      return 'gray'
  }
}

/**
 * The part of the audit page a link can carry: `/audit?category=permissions&entityId=<id>&page=2`.
 *
 * Other surfaces link here rather than re-rendering the trail: the Governance Queue sends
 * `?category=permissions`, and the access drawer sends `?entityId=` because a revoked grant is a
 * hard delete whose only record is this trail. The free-text filters stay on the page — they are
 * typed, not linked.
 */
export interface AuditLink {
  /** The category key as the URL carries it, trimmed; null for none. Not yet checked against the served list. */
  category: string | null
  /** 1-based. Anything that is not a positive whole number is page 1. */
  page: number
  /** The one entity the trail is narrowed to; null for all. The server owns its validity (a bad GUID is its 400). */
  entityId: string | null
}

function trimmedOrNull(value: string | null): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

/** Reads the link from the query string. */
export function readAuditLink(params: URLSearchParams): AuditLink {
  const rawPage = params.get('page')?.trim() ?? ''
  const page = /^\d+$/.test(rawPage) ? Number(rawPage) : 1
  return {
    category: trimmedOrNull(params.get('category')),
    page: Number.isSafeInteger(page) && page >= 1 ? page : 1,
    entityId: trimmedOrNull(params.get('entityId')),
  }
}

/**
 * The query string for a link: page 1 and empty values are left out, and any other parameter is kept.
 *
 * Returns a new object; the argument is not changed.
 */
export function writeAuditLink(params: URLSearchParams, link: AuditLink): URLSearchParams {
  const next = new URLSearchParams(params)
  const set = (key: string, value: string | null) => (value ? next.set(key, value) : next.delete(key))
  set('category', trimmedOrNull(link.category))
  set('page', link.page > 1 ? String(link.page) : null)
  set('entityId', trimmedOrNull(link.entityId))
  return next
}

/**
 * What became of the category a link asked for.
 *
 * - `none`: no category asked for.
 * - `pending`: asked, and the served list has not arrived — hold the query rather than show the whole
 *   trail under a link that promised a filtered one.
 * - `applied`: the server publishes it; `applied` is the server's own spelling of the key.
 * - `unknown`: the list arrived without it — all activity, and the key is cleared from the URL.
 * - `unavailable`: the list failed, so the key cannot be checked — all activity, and the URL keeps it,
 *   because the link may well be good once the list loads.
 *
 * Matched trimmed and case-insensitively, as the server matches it (AuditCategories.OperationsFor).
 */
export interface CategoryResolution {
  state: 'none' | 'pending' | 'applied' | 'unknown' | 'unavailable'
  /** The key to filter on. Only ever a key the server published. */
  applied: string | null
  /** The key the URL should carry. */
  inUrl: string | null
}

export function resolveCategory(
  requested: string | null,
  categories: readonly { key: string }[] | undefined,
  failed: boolean,
): CategoryResolution {
  const asked = trimmedOrNull(requested)
  if (!asked) return { state: 'none', applied: null, inUrl: null }
  if (categories) {
    const match = categories.find((c) => c.key.trim().toLowerCase() === asked.toLowerCase())
    return match
      ? { state: 'applied', applied: match.key, inUrl: match.key }
      : { state: 'unknown', applied: null, inUrl: null }
  }
  return failed
    ? { state: 'unavailable', applied: null, inUrl: asked }
    : { state: 'pending', applied: null, inUrl: asked }
}
